/**
 * F-34 copy/paste of floating elements — PURE logic (no React, no DOM, no
 * Yjs): what goes on the clipboard, what comes back off it, and where a
 * duplicate lands.
 *
 * The carrier is the SYSTEM clipboard, not a module variable: Ctrl/Cmd+C fires
 * a real `copy` event and the payload rides it as a prefixed JSON string. That
 * buys two things a module-local stash cannot — the copy survives a reload, and
 * a second tab on the same board can paste it — and it costs nothing, because
 * the prefix keeps the payload out of every other paste path's way (the grid's
 * TSV paste parses it as one meaningless cell, the link-card paste sees a
 * non-URL, the image paste sees text). `parseElementClipboard` is the ONLY
 * reader that recognizes it.
 *
 * `boardId` is stamped in and checked on paste. Payloads reference board-scoped
 * things — an image element's `assetId` lives under `/api/boards/:id/assets`,
 * a cell anchor names rows and columns of THIS board's grid — so a duplicate
 * pasted onto another board would land broken. Refusing the cross-board paste
 * is deliberate: it falls through to the ordinary text paste rather than
 * creating elements that render as holes.
 */
import type {
  Anchor,
  ConnectorInfo,
  ConnectorSide,
  ElementInfo,
  ElementPayload,
  ElementType,
} from "@kola/shared";

import type { BoardRect } from "../../registries/toolbar-actions/index.js";
import { MIN_CONTAINED_OFFSET } from "./contained.js";

/**
 * Clipboard format marker. Versioned: an older client that meets a newer
 * payload fails `parseElementClipboard` and falls through to a plain text
 * paste, which is the correct degradation for a copy it cannot honor.
 */
export const ELEMENT_CLIPBOARD_PREFIX = "kola/elements@1:";

/** How far each successive paste of the same payload is offset (board units). */
export const PASTE_OFFSET_BOARD_PX = 24;

/**
 * One copied element. `ref` is its ORIGINAL id — the key connectors join on.
 * `x`/`y` is the element's RESOLVED board position at copy time (not its
 * `x`/`y` keys, which are only a fallback cache for a cell-anchored element),
 * so a copy that has to degrade to a board anchor lands where the user saw it.
 */
export interface ClipboardElement {
  ref: string;
  type: ElementType;
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: number;
  anchor: Anchor;
  payload: ElementPayload;
}

/** A copied connector, in terms of the copied elements' `ref`s. */
export interface ClipboardConnector {
  from: { ref: string; side: ConnectorSide };
  to: { ref: string; side: ConnectorSide };
}

export interface ElementClipboard {
  boardId: string;
  elements: ClipboardElement[];
  connectors: ClipboardConnector[];
}

/**
 * Build the payload for a selection. Connectors are carried only when BOTH
 * ends are in the selection: a line to an element that was not copied has
 * nothing to attach its other end to, and silently re-pointing it at the
 * original would wire the duplicate into the source's graph — surprising, and
 * impossible to undo as one thing.
 */
export function buildElementClipboard(
  boardId: string,
  elements: readonly ElementInfo[],
  connectors: readonly ConnectorInfo[],
  resolveRect: (element: ElementInfo) => BoardRect,
): ElementClipboard {
  const refs = new Set(elements.map((el) => el.id));
  return {
    boardId,
    elements: elements.map((el) => {
      const rect = resolveRect(el);
      return {
        ref: el.id,
        type: el.type,
        x: rect.x,
        y: rect.y,
        w: el.w,
        h: el.h,
        rotation: el.rotation,
        anchor: el.anchor,
        payload: el.payload,
      };
    }),
    connectors: connectors
      .filter((c) => refs.has(c.from.elementId) && refs.has(c.to.elementId))
      .map((c) => ({
        from: { ref: c.from.elementId, side: c.from.side },
        to: { ref: c.to.elementId, side: c.to.side },
      })),
  };
}

export function serializeElementClipboard(clipboard: ElementClipboard): string {
  return ELEMENT_CLIPBOARD_PREFIX + JSON.stringify(clipboard);
}

/**
 * Read a clipboard payload back, or null for anything that is not one of ours
 * (ordinary text, TSV from Sheets, a URL) or is malformed. Never throws: the
 * clipboard is user-controlled input, and a paste that cannot be parsed must
 * fall through to the other paste handlers, not take the board down.
 */
export function parseElementClipboard(text: string): ElementClipboard | null {
  if (!text.startsWith(ELEMENT_CLIPBOARD_PREFIX)) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(text.slice(ELEMENT_CLIPBOARD_PREFIX.length));
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const value = parsed as Partial<ElementClipboard>;
  if (typeof value.boardId !== "string" || !Array.isArray(value.elements)) return null;
  const elements = value.elements.filter(isClipboardElement);
  if (elements.length === 0) return null;
  const refs = new Set(elements.map((el) => el.ref));
  const connectors = (Array.isArray(value.connectors) ? value.connectors : []).filter(
    (c): c is ClipboardConnector =>
      isClipboardConnectorEnd(c) && refs.has(c.from.ref) && refs.has(c.to.ref),
  );
  return { boardId: value.boardId, elements, connectors };
}

function isClipboardElement(value: unknown): value is ClipboardElement {
  if (typeof value !== "object" || value === null) return false;
  const el = value as Partial<ClipboardElement>;
  return (
    typeof el.ref === "string" &&
    typeof el.type === "string" &&
    isFiniteNumber(el.x) &&
    isFiniteNumber(el.y) &&
    isFiniteNumber(el.w) &&
    isFiniteNumber(el.h) &&
    typeof el.anchor === "object" &&
    el.anchor !== null &&
    typeof el.payload === "object" &&
    el.payload !== null
  );
}

function isClipboardConnectorEnd(value: unknown): value is ClipboardConnector {
  if (typeof value !== "object" || value === null) return false;
  const c = value as Partial<ClipboardConnector>;
  return typeof c.from?.ref === "string" && typeof c.to?.ref === "string";
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** Where one copied element lands. `step` is 1 for the first paste, 2… after. */
export interface PastePlacement {
  x: number;
  y: number;
  anchor: Anchor;
}

/**
 * Place a duplicate: the same belonging as the original, nudged down-right so
 * it is visibly a copy rather than perfectly hidden behind its source. Repeated
 * pastes of one payload cascade (`step` grows), the way every canvas tool does
 * it.
 *
 * A CELL-anchored element duplicates INTO THE SAME CELL at a larger offset —
 * "duplicate" means the copy belongs where the original belongs, and the cell
 * grows to fit it under the ordinary F-26/F-36 ratchet. If that cell is gone
 * (the row/column was deleted between copy and paste, or the copy came from
 * another tab that has since diverged), the duplicate degrades to a BOARD
 * anchor at the original's cached board position — the same C4 rule
 * `degradeElementToBoard` applies to a live element, so a paste can never
 * create a zombie pinned to a dead cell.
 */
/**
 * Top-left of the copied SET, in the board units it was copied in. A paste into
 * a chosen cell moves every item by the SAME delta from this point, so the set
 * keeps its internal layout — the same rule a group drag follows.
 */
export function clipboardOrigin(elements: readonly ClipboardElement[]): { x: number; y: number } {
  let x = Infinity;
  let y = Infinity;
  for (const el of elements) {
    if (el.x < x) x = el.x;
    if (el.y < y) y = el.y;
  }
  return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : { x: 0, y: 0 };
}

/** The cell a paste was aimed at, with its board-space top-left. */
export interface PasteCellTarget {
  rowId: string;
  colId: string;
  x: number;
  y: number;
}

/**
 * Place a duplicate INTO A CHOSEN CELL (2026-08-30): the user highlighted a
 * cell before pasting, so that cell is where the copy belongs — whatever the
 * original was anchored to, board or another cell. The set lands at the cell's
 * top-left, inset the same `MIN_CONTAINED_OFFSET` a fresh drop uses so it never
 * sits flush in the corner and hides the cell's border.
 *
 * Repeated pastes cascade from there (`step`), so a second Ctrl/Cmd+V into the
 * same cell is visibly a second copy rather than one hidden behind the other.
 */
export function pasteIntoCellPlacement(
  element: ClipboardElement,
  target: PasteCellTarget,
  origin: { x: number; y: number },
  step: number,
): PastePlacement {
  const inset = MIN_CONTAINED_OFFSET + PASTE_OFFSET_BOARD_PX * (step - 1);
  const dx = Math.round(element.x - origin.x + inset);
  const dy = Math.round(element.y - origin.y + inset);
  return {
    x: Math.round(target.x + dx),
    y: Math.round(target.y + dy),
    anchor: { kind: "cell", rowId: target.rowId, colId: target.colId, dx, dy },
  };
}

export function pastePlacement(
  element: ClipboardElement,
  step: number,
  cellExists: (rowId: string, colId: string) => boolean,
): PastePlacement {
  const offset = PASTE_OFFSET_BOARD_PX * step;
  const x = Math.round(element.x + offset);
  const y = Math.round(element.y + offset);
  const anchor = element.anchor;
  if (anchor.kind === "cell" && cellExists(anchor.rowId, anchor.colId)) {
    return {
      x,
      y,
      anchor: {
        kind: "cell",
        rowId: anchor.rowId,
        colId: anchor.colId,
        dx: Math.round(anchor.dx + offset),
        dy: Math.round(anchor.dy + offset),
      },
    };
  }
  return { x, y, anchor: { kind: "board", x, y } };
}
